/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { AsyncPipe, formatDate } from "@angular/common";
import { Component, Inject } from "@angular/core";
import { FormBuilder, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subject, map, first, of, switchMap } from "rxjs";
import {
    ButtonComponent, FormActionsComponent, FormComponent, FormInputComponent,
    FormOption, FormToggleGroupComponent, ModalComponent, SpinnerComponent, patternValidator, requiredValidator } from "typedb-cloud-framework";
import { Cluster, ClusterPrice, Snapshot } from "../../../concept/cluster";
import { clusterDetailsPath } from "../../../routing/resource-paths";
import { ClusterApi } from "../../../service/cluster/cluster-api.service";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { ApplicationState } from "../../../service/application-state.service";
import { DialogResult } from "../../../service/dialog.service";
import { SnackbarService } from "../../../service/snackbar.service";
import { Router } from "@angular/router";
import { MatMenuModule } from "@angular/material/menu";
import { SpaceApi } from "../../../service/space/space-api.service";
import { generateUniqueId, renderThousandthCentsAsUSD } from "../../../util";

export type ClusterRestoreDialogData = { cluster: Cluster, snapshot: Snapshot };
type RestoreType = "current" | "clone";

@Component({
    selector: "tp-cluster-restore-dialog",
    templateUrl: "./cluster-restore-dialog.component.html",
    styleUrl: "./cluster-restore-dialog.component.scss",
    imports: [
        ModalComponent, AsyncPipe, FormActionsComponent, FormsModule, ReactiveFormsModule,
        FormComponent, MatFormFieldModule, MatInputModule,
        MatMenuModule, FormToggleGroupComponent, SpinnerComponent
    ]
})
export class ClusterRestoreDialogComponent {
    readonly isSubmitting$ = new Subject<boolean>();
    readonly form = this.formBuilder.nonNullable.group({
        restoreType: ["clone" as RestoreType, [requiredValidator]],
    });
    readonly snapshotTimeString = formatDate(this.data.snapshot.createdAt, "d MMM yyyy, HH:mm:ss", "en-US");
    readonly restoreOptions: FormOption<RestoreType>[] = [{ value: "clone", viewValue: "Restore to a clone" }, { value: "current", viewValue: "Restore to this cluster" }];
    readonly price$ = this.clusterApi.getClusterPriceThousandthCentsPerHour(
        { cluster: { ...this.data.cluster, isFree: false }, teamUuid: this.app.requireCurrentTeam().uuid }
    );

    constructor(
        private dialogRef: MatDialogRef<ClusterRestoreDialogComponent, DialogResult>,
        @Inject(MAT_DIALOG_DATA) private data: ClusterRestoreDialogData, private clusterApi: ClusterApi,
        private router: Router, private snackbar: SnackbarService, private app: ApplicationState,
        private spaceApi: SpaceApi, private formBuilder: FormBuilder,
    ) {
    }

    restoreCluster() {
        this.isSubmitting$.next(true);
        if (this.form.value.restoreType === "current") this.restoreToCurrent();
        else if (this.form.value.restoreType === "clone") this.restoreToClone();
        else this.isSubmitting$.next(false);
    }

    restoreToCurrent() {
        this.clusterApi.restoreCluster({ clusterUuid: this.data.cluster.uuid, snapshot: this.data.snapshot.id }).subscribe({
            next: () => {
                this.close();
                this.snackbar.success(`Cluster '${this.data.cluster.id}' is now being restored to ${this.snapshotTimeString}.`);
            },
            error: () => {
                this.isSubmitting$.next(false);
            },
        });
    }

    readonly existingClusters$ = this.spaceApi.listClusters(
        this.data.cluster.space.uuid,
        { pagination: { offset: 0, limit: 1000 }, sorting: { attributeType: "id" } },
        of(),
    ).pipe(
        first(clusters => clusters.initial != undefined),
        map(clusters => clusters.initial!)
    )

    restoreToClone() {
        const cluster = this.data.cluster;
        this.existingClusters$.pipe(
            switchMap((existingClusters) =>
                generateUniqueId(`${cluster.id}-clone`, 0, (id) => of(existingClusters.some(cluster => cluster.id === id)))
            ),
            switchMap((id) => {
                return this.clusterApi.deployCluster({
                    ...cluster,
                    id,
                    sampleDataset: undefined,
                }, this.data.snapshot.id).pipe(map(() => id))
            })
        ).subscribe({
            next: (id) => {
                this.close();
                this.snackbar.success(`Created cluster '${id}' from snapshot taken at ${this.snapshotTimeString}.`);
                this.router.navigate([clusterDetailsPath({ id, space: cluster.space }, this.app.requireCurrentTeam())]);
            },
            error: () => {
                this.isSubmitting$.next(false);
            },
        });
    }

    runningPriceString(price: ClusterPrice) {
        return `${renderThousandthCentsAsUSD(price.running.thousandthCentsPerHour)}/hour`;
    }

    close(result?: DialogResult) {
        this.dialogRef.close(result);
    }
}
